<template>
  <base-dialog v-if="pageExists" :model-value="true" @keydown.esc="closeDialog" size="large" scrollable>
    <form @submit.prevent="saveDialog" style="background-color: inherit">
      <v-card-item>
        <v-card-title class="d-flex">
          <span class="text-h5" data-test-id="title">Edit Page {{ pageNo }}</span>
          <v-spacer></v-spacer>
          <v-menu :close-on-content-click="false">
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" variant="text" color="primary" :prepend-icon="mdiHistory" data-test-id="history">History</v-btn>
            </template>
            <!-- the menu unmounts its content once closed, so each opening fetches the history afresh -->
            <v-card color="background" width="500" max-width="90vw" max-height="70vh" class="overflow-y-auto">
              <v-card-text><ActivityList :pageId="pageId" /></v-card-text>
            </v-card>
          </v-menu>
          <v-menu v-if="!onHoldingArea" v-model="showMove" :close-on-content-click="false">
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" variant="text" color="primary">
                <v-icon start>{{ mdiArrowRight }}</v-icon
                >{{ moveText }}
              </v-btn>
            </template>
            <PageMove :pageId="pageId" v-model:open="showMove" @moved="moved" />
          </v-menu>
        </v-card-title>
      </v-card-item>
      <v-card-text>
        <v-container>
          <v-tabs v-model="tab" color="black" center-active grow show-arrows="mobile" bg-color="background">
            <v-tab v-for="item of tabs" :key="item.name" :to="item.route" :data-label="`${item.name} tab`">
              <v-badge v-if="item.hasBadge" :content="item.badgeContent" :color="item.badgeColor" :dot="item.badgeDot" floating>{{ item.name }}</v-badge>
              <span v-else>{{ item.name }}</span>
            </v-tab>
          </v-tabs>
          <v-window class="mt-4" color="background" v-model="tab">
            <router-view name="tab" @busy="disableSave = $event" @dirty="dirty = true"></router-view>
          </v-window>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <cancel-btn @click="closeDialog" data-label="close page">Close</cancel-btn>
        <submit-btn type="submit" :disabled="disableSave || !dirty" data-label="save page">Save</submit-btn>
      </v-card-actions>
    </form>
    <base-dialog v-model="confirmLeave" @keydown.esc="answerLeave(false)" size="small">
      <v-card-item>
        <v-card-title class="text-h5">Unsaved changes</v-card-title>
      </v-card-item>
      <v-card-text>You have unsaved changes to this page. Leave without saving?</v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <cancel-btn @click="answerLeave(false)" data-label="keep editing">Keep editing</cancel-btn>
        <submit-btn @click="answerLeave(true)" color="error" data-label="discard changes">Discard changes</submit-btn>
      </v-card-actions>
    </base-dialog>
  </base-dialog>
</template>

<script>
import { ref, computed, inject } from "vue"
import { useRouter, onBeforeRouteLeave } from "vue-router"
import { mdiArrowRight, mdiHistory } from "@mdi/js"

import { usePage } from "../use"
import { usePageNumbers } from "@/modules/flatplan/usePageNumbers"
import { useNotification } from "@/modules/notification/use"
import PageMove from "./PageMove"
import ActivityList from "@/modules/activity/components/ActivityList"

import { Bus } from "@/Bus"

import { sessionStorageObj } from "@/utils/storage"

export default {
  name: "EditPageView",
  props: {
    pageId: {
      type: String,
      required: true,
    },
  },
  components: {
    PageMove,
    ActivityList,
  },
  setup(props) {
    let router = useRouter()
    let mobile = inject("mobile")

    let bp_page = usePage()
    let bp_pageNumbers = usePageNumbers()
    let pageNo = bp_pageNumbers.pageNoFor(props.pageId)

    let disableSave = ref(false)
    let dirty = ref(false)
    let showMove = ref(false)
    let newOrder = null
    let moveText = ref("Move")
    let movedPageIds = ref([])

    // we make a copy of the page from the store, and we save it in sessionStorage
    // we are going to have to interface with sessionStorage between tabs
    // as each tab is its own URL, so there is no real parent-child relationship
    // The component saves off the original page, and eventually saves it again to
    // the store.
    let page = bp_page.getById(props.pageId)

    if (page === undefined || page === null) {
      // we may have lost it via LiveUpdate, and we do a browser refresh?
      let bp_notification = useNotification()
      bp_notification.show({
        type: "error",
        message: "That page no longer exists",
      })
      router.push({ name: "editor" })
      return {
        pageExists: false,
      }
    }

    let tab = ref(null)
    let tabs = computed(() => {
      return [
        { name: "Content", route: { name: "page-edit-content", params: { pageId: props.pageId } }, hasBadge: false },
        { name: "Notes", route: { name: "page-edit-notes", params: { pageId: props.pageId } }, hasBadge: !!page.notes, badgeDot: true, badgeColor: "tertiary" },
        { name: "Tags", route: { name: "page-edit-tags", params: { pageId: props.pageId } }, hasBadge: false },
        { name: "Comments", route: { name: "page-edit-comments", params: { pageId: props.pageId } }, hasBadge: page.commentCount > 0, badgeContent: page.commentCount, badgeColor: "grey" },
      ]
    })

    sessionStorageObj.setItem("editPage", {
      pageId: props.pageId,
      content: { data: { fragments: page.fragments, template: page.template }, dirty: false },
      tags: { data: { tagRefs: page.tagRefs, createTags: [] }, dirty: false },
      thumbnail: { data: page.thumbnail ? page.thumbnail : {}, dirty: false }, // (original used to display resized preview)
      commentBody: { data: "", dirty: false },
      notes: { data: page.notes, dirty: false },
    })

    const moved = ({ order, affectedPageIds, description }) => {
      newOrder = order
      moveText.value = description
      movedPageIds.value = affectedPageIds
      dirty.value = true
    }

    const closeDialog = () => {
      let returnTo = "flatplan-show"
      let query = router.currentRoute.value.query

      if (query.returnTo) {
        returnTo = query.returnTo
      }
      router.push({ name: returnTo })
    }

    const sameTags = (a, b) => JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort())

    const collectData = () => {
      let editPageData = sessionStorageObj.getItem("editPage")

      let updateData = {}

      if (editPageData.content && editPageData.content.dirty) {
        updateData.fragments = editPageData.content.data.fragments
        updateData.template = editPageData.content.data.template
      }

      if (editPageData.thumbnail && editPageData.thumbnail.dirty) {
        updateData.thumbnail = editPageData.thumbnail.data
      }

      // the tags tab marks itself dirty as it mounts (in tag-store order), so only count tags that differ from the page's, ignoring order
      if (editPageData.tags && editPageData.tags.dirty && (editPageData.tags.data.createTags.length > 0 || !sameTags(editPageData.tags.data.tagRefs, page.tagRefs))) {
        updateData.tags = editPageData.tags.data
      }

      if (editPageData.notes && editPageData.notes.dirty) {
        updateData.notes = editPageData.notes.data
      }

      if (newOrder) {
        updateData.order = newOrder
        updateData.movedPageIds = movedPageIds.value
      }

      return updateData
    }

    const hasChanges = (updateData) => {
      return updateData.fragments !== undefined || updateData.thumbnail !== undefined || updateData.tags !== undefined || updateData.order !== undefined || updateData.notes !== undefined
    }

    let saved = false

    // the leave confirmation: the navigation waits on the answer
    let confirmLeave = ref(false)
    let resolveLeave = null

    const answerLeave = (leave) => {
      confirmLeave.value = false
      resolveLeave?.(leave)
      resolveLeave = null
    }

    // fires on leaving the :pageId route (Close, Esc, Back), not when switching tabs, which stay within it
    onBeforeRouteLeave(() => {
      if (!saved && hasChanges(collectData())) {
        answerLeave(false) // a navigation still waiting on an earlier answer gives way to this one
        confirmLeave.value = true
        return new Promise((resolve) => (resolveLeave = resolve))
      }
    })

    const saveDialog = () => {
      let updateData = collectData()

      if (hasChanges(updateData)) {
        saved = true
        bp_page.updatePage({ pageId: props.pageId, ...updateData }).then(() => {
          movedPageIds.value.forEach((pageId) => {
            Bus.$emit("page-changed", { pageId })
          })
        })
        closeDialog()
      }
    }

    return {
      pageExists: !!page,
      tab,
      tabs,
      mobile,
      closeDialog,
      saveDialog,
      confirmLeave,
      answerLeave,
      pageNo,
      showMove,
      moved,
      disableSave,
      dirty,
      moveText,
      onHoldingArea: computed(() => {
        return bp_page.onHoldingArea.value.findIndex((p) => p._id === props.pageId) !== -1
      }),
      mdiArrowRight,
      mdiHistory,
    }
  },
}
</script>

<style scoped></style>
